import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import EnrolmentTable from "@/components/EnrolmentTable";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ChevronDown, ClipboardPlus, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const CATEGORIES = ["Health", "Education", "Discipline", "Attendance", "Family", "Financial", "Social", "Special Skill"];
const FOLLOW_UP_STATUSES = ["Open", "In Progress", "Follow-up Required", "Resolved"];
const EMPTY_RECORD = {
  student_name: "",
  form: "",
  student_class: "",
  sport: "",
  dorm: "",
  class_teacher: "",
  coach: "",
  category: [],
  description: "",
  minda_sihat_scoring: null,
  intervention: "",
  follow_up_status: "Open",
  guardian_note: "",
};
const DEFAULT_SUMMARY = {
  columns: [
    "Jumlah pelajar asrama",
    "Jumlah pelajar perempuan asrama",
    "Jumlah pelajar lelaki asrama",
  ],
  rows: [
    "Jumlah keseluruhan",
    "Pelajar Tingkatan 1",
    "Pelajar Tingkatan 2",
    "Pelajar Tingkatan 3",
    "Pelajar Tingkatan 4",
    "Pelajar Tingkatan 5",
    "Pelajar Tingkatan 6",
    "Pelajar Melayu",
    "Pelajar India",
    "Pelajar Cina",
    "Pelajar Lain",
    "Pelajar Islam",
    "Pelajar Hindu",
    "Pelajar Buddha",
    "Pelajar Kristian",
    "Pelajar Lain",
  ].map((label) => ({ label, cells: ["", "", ""] })),
};
const SUMMARY_GROUPS = ["All groups", "Total", "Form", "Ethnicity", "Religion", "Other"];

const categoryStyle = {
  Health: "border-rose-300 bg-rose-50 text-rose-800",
  Education: "border-sky-300 bg-sky-50 text-sky-800",
  Discipline: "border-orange-300 bg-orange-50 text-orange-800",
  Attendance: "border-violet-300 bg-violet-50 text-violet-800",
  Family: "border-amber-300 bg-amber-50 text-amber-800",
  Financial: "border-emerald-300 bg-emerald-50 text-emerald-800",
  Social: "border-cyan-300 bg-cyan-50 text-cyan-800",
};

const statusStyle = {
  Open: "border-rose-300 bg-rose-50 text-rose-800",
  "In Progress": "border-sky-300 bg-sky-50 text-sky-800",
  "Follow-up Required": "border-amber-300 bg-amber-50 text-amber-800",
  Resolved: "border-emerald-300 bg-emerald-50 text-emerald-800",
};

const scoreStyle = {
  Red: "border-red-300 bg-red-50 text-red-800",
  Yellow: "border-yellow-300 bg-yellow-50 text-yellow-800",
  Green: "border-green-300 bg-green-50 text-green-800",
};

const normalizeCategories = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (!value) {
    return [];
  }
  return [value];
};

const SELECT_CLASS = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";
const CHART_COLORS = ["#0f766e", "#e11d48", "#2563eb", "#d97706", "#7c3aed", "#0891b2", "#65a30d", "#db2777", "#4f46e5", "#ea580c"];

function parseSummaryNumber(value) {
  const text = new DOMParser().parseFromString(value || "", "text/html").body.textContent || "";
  const match = text.match(/\d[\d,]*/);
  return match ? Number(match[0].replace(/,/g, "")) : 0;
}

function summaryGroupForLabel(label) {
  if (/jumlah keseluruhan|total|overall/i.test(label)) return "Total";
  if (/tingkatan|form|grade/i.test(label)) return "Form";
  if (/melayu|india|cina|ethnicity|ethnic|malay|indian|chinese/i.test(label)) return "Ethnicity";
  if (/islam|hindu|buddha|kristian|religion|christian/i.test(label)) return "Religion";
  return "Other";
}

export default function StudentSupportRecord() {
  const { user } = useAuth();
  const [section, setSection] = useState("statistics");
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ ...DEFAULT_SUMMARY, updated_at: null });
  const [summaryDraft, setSummaryDraft] = useState(null);
  const [summaryEditing, setSummaryEditing] = useState(false);
  const [summarySaving, setSummarySaving] = useState(false);
  const [editingSummaryCell, setEditingSummaryCell] = useState(null);
  const [selectedStatisticRowIndex, setSelectedStatisticRowIndex] = useState(0);
  const [summarySearch, setSummarySearch] = useState("");
  const [summaryGroupFilter, setSummaryGroupFilter] = useState("All groups");
  const [form, setForm] = useState(EMPTY_RECORD);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All categories");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const canEditSummary = user?.role === "admin" || ["Warden", "Hostel Supervisor"].includes(user?.staff_role);

  const loadRecords = async () => {
    try {
      const response = await api.get("/student-support-records");
      setRecords(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load student records");
    }
  };

  useEffect(() => { loadRecords(); }, []);

  const loadSummary = async () => {
    try {
      const response = await api.get("/student-support-summary");
      setSummary(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load student summary");
    }
  };

  useEffect(() => { loadSummary(); }, []);

  const startSummaryEdit = () => {
    setSummaryDraft({
      columns: [...summary.columns],
      rows: summary.rows.map((row) => ({ label: row.label, cells: [...row.cells] })),
    });
    setEditingSummaryCell(null);
    setSummaryEditing(true);
  };

  const cancelSummaryEdit = () => {
    setSummaryDraft(null);
    setEditingSummaryCell(null);
    setSummaryEditing(false);
  };

  const saveSummary = async () => {
    setSummarySaving(true);
    try {
      const response = await api.put("/student-support-summary", summaryDraft);
      setSummary(response.data);
      setSummaryDraft(null);
      setEditingSummaryCell(null);
      setSummaryEditing(false);
      toast.success("Student summary updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save student summary");
    } finally {
      setSummarySaving(false);
    }
  };

  const updateSummary = (updater) => setSummaryDraft((current) => updater(current));
  const addSummaryRow = () => updateSummary((current) => ({
    ...current,
    rows: [...current.rows, { label: "Baris baharu", cells: current.columns.map(() => "") }],
  }));
  const removeSummaryRow = (rowIndex) => updateSummary((current) => ({
    ...current,
    rows: current.rows.filter((_, index) => index !== rowIndex),
  }));
  const addSummaryColumn = () => updateSummary((current) => ({
    columns: [...current.columns, "Lajur baharu"],
    rows: current.rows.map((row) => ({ ...row, cells: [...row.cells, ""] })),
  }));
  const removeSummaryColumn = (columnIndex) => updateSummary((current) => ({
    columns: current.columns.filter((_, index) => index !== columnIndex),
    rows: current.rows.map((row) => ({ ...row, cells: row.cells.filter((_, index) => index !== columnIndex) })),
  }));
  const updateSummaryCell = (rowIndex, columnIndex, value) => updateSummary((current) => ({
    ...current,
    rows: current.rows.map((row, index) => index === rowIndex
      ? { ...row, cells: row.cells.map((cell, cellIndex) => cellIndex === columnIndex ? value : cell) }
      : row),
  }));

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_RECORD);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm({
      ...EMPTY_RECORD,
      ...Object.fromEntries(Object.keys(EMPTY_RECORD).map((key) => [key, record[key] ?? EMPTY_RECORD[key]])),
      category: normalizeCategories(record.category),
      description: record.description || record.issue_description || "",
    });
    setDialogOpen(true);
  };

  const saveRecord = async () => {
    const selectedCategories = normalizeCategories(form.category);
    const description = (form.description || "").trim();

    if (!form.student_name.trim() || !description) {
      toast.error("Student name and description are required");
      return;
    }
    if (selectedCategories.length === 0) {
      toast.error("Select at least one category");
      return;
    }

    const payload = {
      ...form,
      category: selectedCategories,
      description,
      issue_description: description,
    };

    try {
      if (editingId) {
        await api.put(`/student-support-records/${editingId}`, payload);
        toast.success("Student record updated");
      } else {
        await api.post("/student-support-records", payload);
        toast.success("Student added to support records");
      }
      setDialogOpen(false);
      await loadRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save student record");
    }
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Remove the support record for ${record.student_name}?`)) return;
    try {
      await api.delete(`/student-support-records/${record.id}`);
      setRecords((current) => current.filter((item) => item.id !== record.id));
      toast.success("Student record removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove student record");
    }
  };

  const canManage = (record) => user?.role === "admin" || user?.id === record.author_id;
  const normalizedSearch = search.trim().toLowerCase();
  const visibleRecords = records.filter((record) => {
    const description = record.description || record.issue_description || "";
    const categories = normalizeCategories(record.category);
    const matchesSearch = !normalizedSearch || [
      record.student_name, record.form, record.student_class, record.sport, record.dorm,
      record.class_teacher, record.coach, description, record.intervention,
      record.guardian_note,
    ].some((value) => (value || "").toLowerCase().includes(normalizedSearch));
    return matchesSearch
      && (categoryFilter === "All categories" || categories.includes(categoryFilter))
      && (statusFilter === "All statuses" || record.follow_up_status === statusFilter);
  });

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const toggleCategory = (category) => {
    setForm((current) => {
      const selected = normalizeCategories(current.category);
      const next = selected.includes(category)
        ? selected.filter((item) => item !== category)
        : [...selected, category];
      return { ...current, category: next };
    });
  };
  const selectedCategories = normalizeCategories(form.category);
  const displayedSummaryRows = summaryEditing ? summaryDraft.rows : summary.rows;
  const displayedSummaryColumns = summaryEditing ? summaryDraft.columns : summary.columns;
  const filteredSummaryRows = displayedSummaryRows
    .map((row, rowIndex) => ({ row, rowIndex, group: summaryGroupForLabel(row.label) }))
    .filter(({ row, group }) => (
      row.label.toLowerCase().includes(summarySearch.trim().toLowerCase())
      && (summaryGroupFilter === "All groups" || group === summaryGroupFilter)
    ));
  const selectedStatisticRow = summary.rows[selectedStatisticRowIndex] || summary.rows[0];
  const genderColumnIndexes = summary.columns
    .map((column, index) => (/perempuan|lelaki|female|male/i.test(column) ? index : -1))
    .filter((index) => index >= 0);
  const chartColumnIndexes = genderColumnIndexes.length >= 2
    ? genderColumnIndexes
    : summary.columns.map((_, index) => index);
  const statisticChartData = selectedStatisticRow
    ? chartColumnIndexes.map((columnIndex) => ({
      name: summary.columns[columnIndex],
      value: parseSummaryNumber(selectedStatisticRow.cells[columnIndex]),
    })).filter((item) => item.value > 0)
    : [];
  const statisticChartTotal = statisticChartData.reduce((total, item) => total + item.value, 0);

  return (
    <div className="space-y-7" data-testid="student-support-records-page">
      <header className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <ClipboardPlus className="h-4 w-4" /> Student Welfare
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold">Student Record</h1>
        </div>
      </header>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList data-testid="student-record-sections">
          <TabsTrigger value="statistics" data-testid="student-statistics-tab">Student&apos;s Statistics</TabsTrigger>
          <TabsTrigger value="records" data-testid="student-records-tab">Student Records</TabsTrigger>
        </TabsList>
      </Tabs>

      {section === "statistics" && (<EnrolmentTable canEdit={canEditSummary} />)}

      {false && (
      <section className="space-y-4 border-t-4 border-sky-500 pt-4" aria-labelledby="student-count-summary-title" data-testid="student-count-summary">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="student-count-summary-title" className="font-display text-xl font-bold">Student&apos;s Statistics</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Kemaskini terakhir: {summary.updated_at
                ? new Date(summary.updated_at).toLocaleString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "long", timeStyle: "short" })
                : "Belum dikemas kini"}
            </p>
          </div>
          {canEditSummary && (
            <div className="flex flex-wrap gap-2">
              {summaryEditing ? (
                <>
                  <Button variant="outline" onClick={cancelSummaryEdit} data-testid="student-summary-cancel-button"><X className="h-4 w-4" /> Batal</Button>
                  <Button onClick={saveSummary} disabled={summarySaving} data-testid="student-summary-save-button">{summarySaving ? "Menyimpan..." : "Simpan jadual"}</Button>
                </>
              ) : (
                <Button variant="outline" onClick={startSummaryEdit} data-testid="student-summary-edit-button"><Pencil className="h-4 w-4" /> Edit jadual</Button>
              )}
            </div>
          )}
        </div>

        {summaryEditing && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={addSummaryRow} data-testid="student-summary-add-row"><Plus className="h-4 w-4" /> Tambah baris</Button>
            <Button variant="outline" size="sm" onClick={addSummaryColumn} disabled={summaryDraft.columns.length >= 10} data-testid="student-summary-add-column"><Plus className="h-4 w-4" /> Tambah lajur</Button>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid flex-1 gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <Input
              value={summarySearch}
              onChange={(event) => setSummarySearch(event.target.value)}
              placeholder="Search statistic labels"
              aria-label="Search student statistics"
              data-testid="student-summary-search"
            />
            <select
              className={SELECT_CLASS}
              value={summaryGroupFilter}
              onChange={(event) => setSummaryGroupFilter(event.target.value)}
              aria-label="Filter statistics by group"
              data-testid="student-summary-group-filter"
            >
              {SUMMARY_GROUPS.map((group) => <option key={group}>{group}</option>)}
            </select>
          </div>
          <p className="text-sm text-muted-foreground" aria-live="polite" data-testid="student-summary-result-count">
            Showing {filteredSummaryRows.length} of {displayedSummaryRows.length} statistics
          </p>
        </div>

        <div className="overflow-x-auto border-y border-border">
          <table className="w-full min-w-[680px] border-collapse text-sm" data-testid="student-summary-table">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-xs">
                <th scope="col" className="min-w-44 px-3 py-2.5 text-left font-semibold">Kategori</th>
                {displayedSummaryColumns.map((column, columnIndex) => (
                  <th key={`summary-column-${columnIndex}`} scope="col" className="min-w-44 px-3 py-2.5 text-left align-top font-semibold">
                    {summaryEditing ? (
                      <div className="flex items-center gap-2">
                        <Input value={column} onChange={(event) => updateSummary((current) => ({
                          ...current,
                          columns: current.columns.map((item, index) => index === columnIndex ? event.target.value : item),
                        }))} aria-label={`Nama lajur ${columnIndex + 1}`} data-testid={`student-summary-column-${columnIndex}`} />
                        <Button variant="ghost" size="icon" onClick={() => removeSummaryColumn(columnIndex)} disabled={summaryDraft.columns.length <= 1} aria-label={`Buang lajur ${columnIndex + 1}`} data-testid={`student-summary-remove-column-${columnIndex}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : column}
                  </th>
                ))}
                {summaryEditing && <th scope="col" className="w-12 px-2 py-3"><span className="sr-only">Tindakan</span></th>}
              </tr>
            </thead>
            <tbody>
              {filteredSummaryRows.map(({ row, rowIndex }) => (
                <tr key={`summary-row-${rowIndex}`} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`student-summary-row-${rowIndex}`}>
                  <th scope="row" className="px-3 py-2 text-left align-top font-medium">
                    {summaryEditing ? (
                      <Input value={row.label} onChange={(event) => updateSummary((current) => ({
                        ...current,
                        rows: current.rows.map((item, index) => index === rowIndex ? { ...item, label: event.target.value } : item),
                      }))} aria-label={`Nama baris ${rowIndex + 1}`} data-testid={`student-summary-row-label-${rowIndex}`} />
                    ) : row.label}
                  </th>
                  {row.cells.map((cell, columnIndex) => (
                    <td key={`summary-cell-${rowIndex}-${columnIndex}`} className="px-3 py-2 align-top">
                      {summaryEditing && editingSummaryCell?.rowIndex === rowIndex && editingSummaryCell?.columnIndex === columnIndex ? (
                        <div className="space-y-2">
                          <RichTextEditor
                            value={cell}
                            onChange={(value) => updateSummaryCell(rowIndex, columnIndex, value)}
                            ariaLabel={`Nilai ${row.label}, ${summaryDraft.columns[columnIndex]}`}
                          />
                          <Button size="sm" variant="outline" onClick={() => setEditingSummaryCell(null)} data-testid={`student-summary-cell-done-${rowIndex}-${columnIndex}`}>Selesai</Button>
                        </div>
                      ) : (
                        <div className="flex min-h-10 items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: cell || "" }} />
                          {summaryEditing && (
                            <Button variant="ghost" size="icon" onClick={() => setEditingSummaryCell({ rowIndex, columnIndex })} aria-label={`Edit ${row.label}, ${summaryDraft.columns[columnIndex]}`} title="Edit nilai dan format" data-testid={`student-summary-cell-edit-${rowIndex}-${columnIndex}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                  {summaryEditing && (
                    <td className="px-2 py-2 align-top">
                      <Button variant="ghost" size="icon" onClick={() => removeSummaryRow(rowIndex)} aria-label={`Buang ${row.label}`} data-testid={`student-summary-remove-row-${rowIndex}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {filteredSummaryRows.length === 0 && (
                <tr>
                  <td colSpan={displayedSummaryColumns.length + (summaryEditing ? 2 : 1)} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    No statistics match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid gap-5 border-b border-border pb-5 md:grid-cols-[minmax(12rem,1fr)_minmax(16rem,1fr)] md:items-center">
          <div className="space-y-3">
            <h3 className="font-semibold">Student breakdown</h3>
            <Label htmlFor="student-statistic-area">Statistic area</Label>
            <select
              id="student-statistic-area"
              className={SELECT_CLASS}
              value={selectedStatisticRowIndex}
              onChange={(event) => setSelectedStatisticRowIndex(Number(event.target.value))}
              aria-label="Select student statistic area"
              data-testid="student-statistic-area-select"
            >
              {summary.rows.map((row, index) => (
                <option key={`statistic-area-${index}`} value={index}>{row.label}</option>
              ))}
            </select>
            {statisticChartData.length > 0 ? (
              <ul className="space-y-2 text-sm" data-testid="student-statistic-chart-legend">
                {statisticChartData.map((item, index) => (
                  <li key={`${item.name}-${index}`} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                      <span className="truncate">{item.name}</span>
                    </span>
                    <span className="shrink-0 font-medium">
                      {item.value.toLocaleString()} ({((item.value / statisticChartTotal) * 100).toFixed(1)}%)
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No numeric data available for this statistic area.</p>
            )}
          </div>
          {statisticChartData.length > 0 && (
            <div className="h-56 w-full" data-testid="student-statistic-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statisticChartData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={88} paddingAngle={2}>
                    {statisticChartData.map((item, index) => (
                      <Cell key={`${item.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [
                    `${Number(value).toLocaleString()} (${((Number(value) / statisticChartTotal) * 100).toFixed(1)}%)`,
                    name,
                  ]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>
      )}

      {section === "records" && (
      <section className="space-y-4" aria-labelledby="student-records-title" data-testid="student-records-section">
      <div className="flex flex-wrap items-end justify-between gap-3 border-t-4 border-emerald-500 pt-4">
        <h2 id="student-records-title" className="font-display text-xl font-bold">Student Records</h2>
        <Button onClick={openCreate} data-testid="student-support-add-button">
          <Plus className="h-4 w-4" /> Add student
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(14rem,1fr)_12rem_12rem]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search students or record details" aria-label="Search student records" className="pl-9" />
        </div>
        <select className={SELECT_CLASS} aria-label="Filter by category" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
          <option>All categories</option>
          {CATEGORIES.map((category) => <option key={category}>{category}</option>)}
        </select>
        <select className={SELECT_CLASS} aria-label="Filter by follow-up status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option>All statuses</option>
          {FOLLOW_UP_STATUSES.map((status) => <option key={status}>{status}</option>)}
        </select>
      </div>

      {visibleRecords.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground">
          {records.length === 0 ? "No students are currently listed for support." : "No records match your search or filters."}
        </div>
      ) : (
        <div className="divide-y divide-border border-t-4 border-amber-500 border-b border-border pt-4">
          {visibleRecords.map((record) => {
            const categories = normalizeCategories(record.category);
            const description = record.description || record.issue_description || "";
            return (
              <article key={record.id} className="py-5" data-testid={`student-support-record-${record.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-display text-xl font-bold">{record.student_name}</h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {categories.length > 0 ? categories.map((category) => (
                      <span key={`${record.id}-${category}`} className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${categoryStyle[category] || "border-border bg-secondary text-secondary-foreground"}`}>
                        {category}
                      </span>
                    )) : (
                      <span className="rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
                        Unspecified
                      </span>
                    )}
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyle[record.follow_up_status] || "border-border bg-secondary text-secondary-foreground"}`}>
                      {record.follow_up_status}
                    </span>
                    {canManage(record) && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(record)} aria-label={`Edit ${record.student_name} support record`} title="Edit record">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteRecord(record)} aria-label={`Remove ${record.student_name} support record`} title="Remove record">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {description && <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{description}</p>}
                <details className="group mt-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between border-y border-border py-3 text-sm font-medium [&::-webkit-details-marker]:hidden">
                    More student details
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <dl className="grid gap-x-6 gap-y-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Detail label="Form" value={record.form} />
                    <Detail label="Class" value={record.student_class} />
                    <Detail label="Dorm" value={record.dorm} />
                    <Detail label="Sport" value={record.sport} />
                    <Detail label="Class teacher" value={record.class_teacher} />
                    <Detail label="Coach" value={record.coach} />
                    <Detail
                      label="Minda Sihat scoring"
                      value={record.minda_sihat_scoring ? (
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${scoreStyle[record.minda_sihat_scoring] || "border-border bg-secondary text-secondary-foreground"}`}>
                          {record.minda_sihat_scoring}
                        </span>
                      ) : ""}
                    />
                    <Detail label="Intervention" value={record.intervention} />
                    <Detail label="Guardian note" value={record.guardian_note} />
                  </dl>
                  <p className="pb-2 text-xs text-muted-foreground">Recorded by {record.author_name}</p>
                </details>
              </article>
            );
          })}
        </div>
      )}
      </section>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit student record" : "Add student record"}</DialogTitle>
            <DialogDescription>Enter the student details and current support information.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Student name" required><Input value={form.student_name} onChange={(event) => updateField("student_name", event.target.value)} /></Field>
            <Field label="Form"><Input value={form.form} onChange={(event) => updateField("form", event.target.value)} /></Field>
            <Field label="Class"><Input value={form.student_class} onChange={(event) => updateField("student_class", event.target.value)} /></Field>
            <Field label="Sport"><Input value={form.sport} onChange={(event) => updateField("sport", event.target.value)} /></Field>
            <Field label="Dorm"><Input value={form.dorm} onChange={(event) => updateField("dorm", event.target.value)} /></Field>
            <Field label="Class teacher"><Input value={form.class_teacher} onChange={(event) => updateField("class_teacher", event.target.value)} /></Field>
            <Field label="Coach"><Input value={form.coach} onChange={(event) => updateField("coach", event.target.value)} /></Field>
            <Field label="Category" required className="sm:col-span-2">
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3" id={`student-support-${"category"}`}>
                {CATEGORIES.map((category) => {
                  const active = selectedCategories.includes(category);
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className={`flex h-10 items-center justify-center rounded-md border px-3 text-sm font-medium transition ${active ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-foreground hover:bg-accent"}`}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Follow-up status" required>
              <select className={SELECT_CLASS} value={form.follow_up_status} onChange={(event) => updateField("follow_up_status", event.target.value)}>
                {FOLLOW_UP_STATUSES.map((status) => <option key={status}>{status}</option>)}
              </select>
            </Field>
            <Field label="Minda Sihat scoring">
              <select
                className={SELECT_CLASS}
                value={form.minda_sihat_scoring || ""}
                onChange={(event) => updateField("minda_sihat_scoring", event.target.value || null)}
              >
                <option value="">Not scored</option>
                <option value="Red">Red</option>
                <option value="Yellow">Yellow</option>
                <option value="Green">Green</option>
              </select>
            </Field>
            <Field label="Description" required className="sm:col-span-2">
              <Textarea rows={3} value={form.description} onChange={(event) => updateField("description", event.target.value)} />
            </Field>
            <Field label="Intervention" className="sm:col-span-2">
              <Textarea rows={3} value={form.intervention} onChange={(event) => updateField("intervention", event.target.value)} />
            </Field>
            <Field label="Guardian note" className="sm:col-span-2">
              <Textarea rows={3} value={form.guardian_note} onChange={(event) => updateField("guardian_note", event.target.value)} />
            </Field>
          </div>

          <DialogFooter>
            <Button onClick={saveRecord} data-testid="student-support-save-button">{editingId ? "Save changes" : "Add student"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, className = "", children }) {
  const inputId = `student-support-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={inputId}>{label}{required ? " *" : ""}</Label>
      {React.cloneElement(children, { id: inputId })}
    </div>
  );
}

function Detail({ label, value, wide }) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm">{value || "Not provided"}</dd>
    </div>
  );
}
